import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  ShieldCheck,
  BookOpen,
  Map,
  Lock,
  Building2,
  Mail,
  Check,
} from 'lucide-react'
import { useStatePack } from '@/lib/state-context'
import GrowingLossesChart from '@/components/about/GrowingLossesChart'
import { resolveSite, siteAudience, siteBrand } from '@/lib/site'
import QatarAbout from '@/components/qatar/QatarAbout'
import UmbrellaAbout from '@/components/umbrella/UmbrellaAbout'

export const Route = createFileRoute('/about')({
  component: AboutRoute,
})

function AboutRoute() {
  // Apex shows the platform/company story; NASAA keeps the consumer about page.
  const site = resolveSite()
  if (site === 'qatar') return <QatarAbout />
  return site === 'umbrella' ? <UmbrellaAbout /> : <AboutPage />
}

function AboutPage() {
  const pack = useStatePack()
  const stateName = pack.name ?? 'the United States'
  // NASAA is a per-state product; the association builds reframe the
  // states-specific copy around members. Keyed off the audience rather than
  // `!== 'nasaa'`, which used to hand every non-NASAA face the military copy.
  const audience = siteAudience()
  const brand = siteBrand()
  const isMember = audience !== 'state'
  const partnerNoun = isMember ? 'member' : 'state'
  // The partner solicitation is a B2B pitch. It belongs on the association
  // faces the partner themselves browses, not on the consumer face a member
  // is sent to.
  const showPartnerBand = audience !== 'laterLife'

  return (
    <div className="bg-white">
      {/* ── HERO ───────────────────────────────────────── */}
      {/* about-hero lets a tenant theme (e.g. .theme-amba) override the
          hardcoded dark surface, which the violet ramp can't reach. */}
      <section className="about-hero bg-[#0a0620] text-white">
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 sm:pt-20">
          <p className="text-sm font-semibold tracking-tight text-white/55">
            About Us
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl">
            We help people avoid scams.
          </h1>
          <div className="mt-3 h-[3px] w-12 rounded-full bg-violet-400" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
            {brand.name} was created to help people understand, spot, and
            respond to modern scams before money is lost.
          </p>
        </div>
      </section>

      {/* ── 1. THE PROBLEM ─────────────────────────────── */}
      <section className="border-y border-slate-200 bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold tracking-tight text-slate-500">
              The problem
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Fraud losses are climbing fast.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Reported losses to the FBI have climbed from{' '}
              <strong className="font-semibold text-slate-900">
                $3.5 billion in 2019
              </strong>{' '}
              to{' '}
              <strong className="font-semibold text-slate-900">
                more than $20 billion in 2025
              </strong>
              ,{' '}
              <strong className="font-semibold text-slate-900">
                almost six times higher in just six years
              </strong>
              . Criminals now use stolen data, emotional pressure, fake
              authority, convincing websites, AI-generated messages, and
              urgent payment requests to make their stories feel real.
            </p>
          </div>

          <div className="mt-12">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-sm font-semibold tracking-tight text-slate-500">
                  Reported losses to the FBI
                </p>
                <p className="mt-1 text-base font-semibold text-slate-900">
                  Annual internet crime losses, United States
                </p>
              </div>
              <p className="text-xs text-slate-400">
                Source: FBI IC3 Annual Reports
              </p>
            </div>
            <div className="mt-6">
              <GrowingLossesChart />
            </div>
          </div>

        </div>
      </section>

      {/* ── 2. OUR SOLUTION ────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold tracking-tight text-slate-500">
            Our solution
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Clear training, practical tools, and guidance you can actually use.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            {brand.name} focuses on the human side of fraud. We teach scam
            defense as a skill: something people can learn, practice, and
            improve over time. We’re not trying to make people paranoid. We
            want them calmer, more confident, and better prepared.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-10">
          <div className="flex flex-col">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
              <BookOpen className="h-5 w-5" />
            </span>
            <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">
              A plain-English course
            </h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              Our Scam Defense Fundamentals course teaches the patterns behind
              modern scams. You will learn how scammers create urgency, how
              they impersonate trusted people and organizations, why certain
              payment methods are risky, and what to do if something feels
              wrong.
            </p>
            <Link
              to="/basics/$basicsSlug"
              params={{ basicsSlug: 'new-age-of-scams' }}
              className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-800"
            >
              Start the course
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <div className="flex flex-col">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
              {isMember ? <ShieldCheck className="h-5 w-5" /> : <Map className="h-5 w-5" />}
            </span>
            {audience === 'military' ? (
              <>
                <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">
                  The scams that target military life
                </h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  Military life brings scams that exploit deployments, moves,
                  steady pay, and federal benefits. Our scam library breaks down
                  how each one works, what to watch for, and what to do if one
                  reaches you.
                </p>
                <Link
                  to="/scam-library"
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-800"
                >
                  Browse the scam library
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </>
            ) : audience === 'laterLife' ? (
              <>
                <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">
                  The scams that pick their targets
                </h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  A handful of approaches are built around later life: your
                  family, your benefits, your savings. Our scam library breaks
                  down how each one works, what to watch for, and what to do if
                  one reaches you.
                </p>
                <Link
                  to="/scam-library"
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-800"
                >
                  Browse the scam library
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </>
            ) : (
              <>
                <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">
                  State-by-state fraud insights
                </h3>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  Scams don’t look the same everywhere. Our state pages, built on
                  publicly available FBI IC3 data, show what kinds of fraud are
                  affecting people near you, how losses are happening, and where
                  to report suspicious activity.
                </p>
                <Link
                  to="/states"
                  className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-800"
                >
                  Browse all 51 states
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── 3. WHY WE EXIST ────────────────────────────── */}
      <section className="border-y border-slate-200 bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-sm font-semibold tracking-tight text-slate-500">
                Our mission
              </p>
              <h2 className="mt-2 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                Help everyone stay one step ahead of scammers.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                Scams are getting more convincing. Scam defense should be
                getting more accessible. We exist to close that gap.
              </p>
            </div>

            <div>
              <p className="text-base font-semibold text-slate-900">
                We believe good fraud education should be:
              </p>
              <ul className="mt-5 space-y-3 text-base text-slate-600">
                {[
                  'Clear enough for anyone to follow',
                  'Practical enough to use in real life',
                  'Calm rather than fear-based',
                  'Based on real scam patterns',
                  'Focused on prevention, not blame',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERSHIPS ─────────────────────────────────── */}
      {showPartnerBand && (
      <section className="about-partner-band bg-gradient-to-br from-[#1d1145] via-[#1f1750] to-[#2a1d6b] py-20 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-sm font-semibold tracking-tight text-white/55">
                For partners
              </p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Bring ScamDefense to your residents or members.
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">
                {isMember
                  ? 'Military banks, credit unions, and the associations that serve them can co-brand a member page for their customers, route fraud reports to their own line, and feature relevant content.'
                  : 'State agencies, attorney general offices, credit unions, and community banks can co-brand the state page that serves their constituents, route complaints to their own hotline, and feature locally relevant content.'}
              </p>
              <a
                href="mailto:partnerships@scamdefense.org"
                className="about-cta group mt-8 inline-flex items-center gap-3 rounded-2xl bg-violet-500 px-6 py-3.5 text-base font-semibold text-white shadow-[0_15px_40px_-12px_rgba(106,92,255,0.55)] transition-colors hover:bg-violet-400"
              >
                <Mail className="h-4 w-4" />
                <span>partnerships@scamdefense.org</span>
              </a>
            </div>
            <ul className="space-y-4">
              <PartnerLine
                icon={Building2}
                title={`Co-branded ${partnerNoun} page`}
                body="Your seal in the partnership strip. Your hotline at the top of the response section. Your tagline alongside ours."
              />
              <PartnerLine
                icon={ShieldCheck}
                title="Tailored to real threats"
                body={
                  isMember
                    ? 'The same proven course, with examples drawn from the scams that actually target your community and reporting routes built in.'
                    : 'The same proven course, with examples drawn from the scams that actually hit your state and reporting routes built in.'
                }
              />
              <PartnerLine
                icon={Lock}
                title="No data sharing"
                body="We don’t collect personal information from learners, and we never hand any to a partner. You lend your name, never your members’ privacy."
              />
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* ── WHAT IT ISN'T ────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 py-20">
        <p className="text-sm font-semibold tracking-tight text-slate-500">
          A few important notes
        </p>
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          What {brand.name} is not.
        </h2>
        <ul className="mt-8 space-y-3 text-base leading-relaxed text-slate-600">
          <li className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span>
              Not a substitute for legal or financial advice. If you need
              professional guidance, contact a licensed attorney or financial
              advisor in your jurisdiction.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span>
              Not a victim-recovery service. If you’ve already been scammed,
              start with{' '}
              <Link
                to="/basics/$basicsSlug"
                params={{ basicsSlug: 'if-youre-scammed' }}
                className="font-semibold text-violet-600 hover:text-violet-700"
              >
                Chapter 6: If You’re Scammed
              </Link>
              .
            </span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span>
              Not a place that will ever ask for your Social Security number,
              banking details, or account passwords. If something on this site
              ever seems to, treat it as a scam and email us.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span>
              Not affiliated with any law enforcement agency. We link to
              official reporting channels but don’t represent them.
            </span>
          </li>
        </ul>
      </section>

      {/* ── BOTTOM CTA ───────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 sm:p-14">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-[1.4fr_1fr] sm:items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Outsmart scammers.
              </h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg">
                Six short chapters, plain English, real examples. No accounts.
                No ads.{' '}
                {audience === 'military'
                  ? 'Built for service members, families, and veterans.'
                  : audience === 'laterLife'
                    ? 'Built for you and your family.'
                    : `For everyone in ${stateName}.`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:justify-end">
              <Link
                to="/basics/$basicsSlug"
                params={{ basicsSlug: 'new-age-of-scams' }}
                className="about-cta group inline-flex items-center gap-3 rounded-2xl bg-violet-500 px-6 py-3.5 text-base font-semibold text-white shadow-[0_15px_40px_-12px_rgba(106,92,255,0.55)] transition-colors hover:bg-violet-400"
              >
                <span>Start the course</span>
                <span className="about-cta-pip flex h-8 w-8 items-center justify-center rounded-full bg-white text-violet-600 transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
              {isMember ? (
                <Link
                  to="/scam-library"
                  className="text-sm font-semibold text-slate-700 underline-offset-4 hover:underline"
                >
                  Browse the scam library →
                </Link>
              ) : (
                <Link
                  to="/states"
                  className="text-sm font-semibold text-slate-700 underline-offset-4 hover:underline"
                >
                  Find your state →
                </Link>
              )}
            </div>
          </div>
        </div>

        {pack.sponsor && (
          <p className="mt-8 text-center text-sm text-slate-400">
            {pack.sponsor.label}. {brand.name} is the same training nationwide;
            localized examples and reporting agencies are tailored to{' '}
            {pack.name}.
          </p>
        )}
      </section>
    </div>
  )
}

function PartnerLine({
  icon: Icon,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  body: string
}) {
  return (
    <li className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/20 text-violet-200 ring-1 ring-violet-300/20">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-base font-semibold text-white">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-white/70">{body}</p>
      </div>
    </li>
  )
}
